import { useState, useCallback } from 'react';
import {
  isLockedCategory,
  isLockedProduct,
  parsePrice,
  createProductKey,
  parseProductKey
} from '../AppUtils.js';
import {
  SUBCATEGORIES,
  DISPLAY_TYPES,
  BRAND_DEPENDENT_CATEGORIES,
  DISPLAY_DEPENDENT_CATEGORIES,
  isDisplayCategory,
  isWheelRimCategory,
  isSeatsCategory,
  allowsMultipleSelection
} from '../constants.js';

// ============================================================================
// PERFORMANCE OPTIMIZATION: Compatibility score mapping
// ============================================================================
const COMPATIBILITY_SCORES = {
  'preferred': 0,           // Best - matching brand ecosystem
  'neutral': 1,             // Good - no constraints
  'different-brand': 2,     // OK - different brand but no conflicts
  'brand_incompatible': 100,    // Poor - brand conflicts
  'display_incompatible': 101,  // Poor - display type conflicts
  'size_incompatible': 102      // Poor - size conflicts
};

// Hook for managing product selection and business logic - PERFORMANCE OPTIMIZED
export const useProductSelection = (productData, categoryConfig = {}) => {
  const [selectedProducts, setSelectedProducts] = useState({});

  // Get display type from product - with fallback to title parsing
  const getDisplayType = useCallback((product) => {
    // First try the displayTypeCompatibility field
    if (product.displayTypeCompatibility) {
      let compatibility = product.displayTypeCompatibility;
      
      // Handle string format from CSV (e.g., "[\"Triple\"]")
      if (typeof compatibility === 'string') {
        try {
          compatibility = JSON.parse(compatibility);
        } catch (e) {
          return null;
        }
      }
      
      // Handle array format
      if (Array.isArray(compatibility) && compatibility.length > 0) {
        return compatibility[0];
      }
      
      return compatibility;
    }
    
    // FALLBACK: Parse from product name if displayTypeCompatibility is missing
    if (product.name) {
      const name = product.name.toLowerCase();
      if (name.includes('single')) return DISPLAY_TYPES.SINGLE;
      if (name.includes('triple')) return DISPLAY_TYPES.TRIPLE;
      if (name.includes('vr')) return DISPLAY_TYPES.VR;
    }
    
    return null;
  }, []);

  // Check if display types are compatible
  const areDisplayTypesCompatible = useCallback((type1, type2) => {
    // VR is always compatible (no conflicts)
    if (type1 === DISPLAY_TYPES.VR || type2 === DISPLAY_TYPES.VR) return true;
    
    // Single and Triple are only compatible with themselves
    return type1 === type2;
  }, []);

  // Display TYPE compatibility check
  const checkDisplayCompatibility = useCallback((newProduct, selectedProducts) => {
    const checkCategories = DISPLAY_DEPENDENT_CATEGORIES[newProduct.subCategory] || [];
    
    if (checkCategories.length === 0) {
      return { compatible: true, conflicts: [] };
    }
    
    const conflicts = [];
    const newProductDisplayType = getDisplayType(newProduct);
    
    if (!newProductDisplayType) {
      return { compatible: true, conflicts: [] };
    }

    Object.values(selectedProducts).forEach(selectedProduct => {
      const selectedDisplayType = getDisplayType(selectedProduct);
      
      if (selectedDisplayType && checkCategories.includes(selectedProduct.subCategory)) {
        if (!areDisplayTypesCompatible(newProductDisplayType, selectedDisplayType)) {
          conflicts.push(selectedProduct);
        }
      }
    });

    return {
      compatible: conflicts.length === 0,
      conflicts: conflicts,
      newProductType: newProductDisplayType,
      existingType: conflicts.length > 0 ? getDisplayType(conflicts[0]) : null
    };
  }, [getDisplayType, areDisplayTypesCompatible]);

  // Size compatibility checker
  const checkSizeCompatibility = useCallback((newProduct, selectedProducts) => {
    // Only check size compatibility for Display category products
    if (!isDisplayCategory(newProduct.mainCategory)) {
      return { compatible: true, conflicts: [] };
    }

    const conflicts = [];

    // Check each existing product for size conflicts
    for (const existing of Object.values(selectedProducts)) {
      // Skip if not in Display category
      if (!isDisplayCategory(existing.mainCategory)) continue;

      let conflict = null;

      // Case 1: New product is a display, existing is a stand
      if (newProduct.displaySize && (existing.maxDisplaySize || existing.minDisplaySize)) {
        const displaySize = newProduct.displaySize;
        const maxSize = existing.maxDisplaySize;
        const minSize = existing.minDisplaySize;

        if (maxSize && displaySize > maxSize) {
          conflict = {
            type: 'display_too_large',
            message: `${displaySize}" display is too large for stand (max: ${maxSize}")`
          };
        } else if (minSize && displaySize < minSize) {
          conflict = {
            type: 'display_too_small', 
            message: `${displaySize}" display is too small for stand (min: ${minSize}")`
          };
        }
      }

      // Case 2: New product is a stand, existing is a display
      else if ((newProduct.maxDisplaySize || newProduct.minDisplaySize) && existing.displaySize) {
        const displaySize = existing.displaySize;
        const maxSize = newProduct.maxDisplaySize;
        const minSize = newProduct.minDisplaySize;

        if (maxSize && displaySize > maxSize) {
          conflict = {
            type: 'display_too_large',
            message: `Stand cannot support ${displaySize}" display (max: ${maxSize}")`
          };
        } else if (minSize && displaySize < minSize) {
          conflict = {
            type: 'display_too_small',
            message: `Stand cannot support ${displaySize}" display (min: ${minSize}")`
          };
        }
      }

      if (conflict) {
        conflicts.push({
          existingProduct: existing,
          conflictType: conflict.type,
          message: conflict.message
        });
      }
    }

    return { compatible: conflicts.length === 0, conflicts };
  }, []);

  // Find product by slug across all categories
  const findProductBySlug = useCallback((productSlug) => {
    for (const mainCategory of Object.keys(productData)) {
      for (const subCategory of Object.keys(productData[mainCategory])) {
        const found = productData[mainCategory][subCategory].find(
          product => product.slug === productSlug
        );
        if (found) {
          return { ...found, foundInSubCategory: subCategory };
        }
      }
    }
    return null;
  }, [productData]);

  // Enhanced brand compatibility check
  const checkBrandCompatibility = useCallback((newProduct, selectedProducts) => {
    // BYPASS: Never apply brand compatibility to locked categories (Chassis, UPS, etc.)
    if (isLockedCategory(newProduct.mainCategory)) {
      return { 
        compatible: true, 
        conflicts: [], 
        sameBrand: false,
        recommendedBrand: null,
        productBrand: newProduct.brand && newProduct.brand.length > 0 ? newProduct.brand[0] : null
      };
    }
    
    // Find what brand is currently selected in related categories
    const checkCategories = BRAND_DEPENDENT_CATEGORIES[newProduct.subCategory] || [];
    
    // If this product's subcategory is not in the brand-dependent categories, skip compatibility check
    if (checkCategories.length === 0) {
      return { 
        compatible: true, 
        conflicts: [], 
        sameBrand: false,
        recommendedBrand: null,
        productBrand: newProduct.brand && newProduct.brand.length > 0 ? newProduct.brand[0] : null
      };
    }
    
    let recommendedBrand = null;
    let sameBrand = false;
    const conflicts = [];

    // Establish recommended brand from ANY brand-dependent product (wheel base OR wheel rim)
    // This ensures ecosystem is maintained even after switching (when only rim or only base exists)
    Object.values(selectedProducts).forEach(selectedProduct => {
      if (selectedProduct.brand &&
          Array.isArray(selectedProduct.brand) &&
          selectedProduct.brand.length > 0) {

        // Check if this product is in ANY brand-dependent category (not just checkCategories)
        const isSelectedProductBrandDependent = Object.keys(BRAND_DEPENDENT_CATEGORIES).includes(selectedProduct.subCategory);

        if (isSelectedProductBrandDependent) {
          // Set the recommended brand from ANY brand-dependent product
          // This allows wheel rims alone OR wheel bases alone to establish the ecosystem
          if (!recommendedBrand) {
            recommendedBrand = selectedProduct.brand[0];
          }
        }
      }
    });


    // Now check the new product
    if (!newProduct.brand || !Array.isArray(newProduct.brand) || newProduct.brand.length === 0) {
      // If there's a recommended brand ecosystem but this product has no brand, it's incompatible
      if (recommendedBrand) {
        return { 
          compatible: false, 
          conflicts: [], 
          sameBrand: false,
          recommendedBrand: recommendedBrand,
          productBrand: null,
          reason: 'No brand data - incompatible with ecosystem'
        };
      }
      
      // No brand and no ecosystem - neutral
      return { 
        compatible: true, 
        conflicts: [], 
        sameBrand: false,
        recommendedBrand: null,
        productBrand: null
      };
    }

    const newProductBrand = newProduct.brand[0].toLowerCase().trim();

    // Check brand compatibility with selected products
    Object.values(selectedProducts).forEach(selectedProduct => {
      if (selectedProduct.brand &&
          Array.isArray(selectedProduct.brand) &&
          selectedProduct.brand.length > 0) {

        const selectedProductBrand = selectedProduct.brand[0].toLowerCase().trim();

        // Check if this selected product is brand-dependent (wheel base OR wheel rim)
        const isSelectedProductBrandDependent = Object.keys(BRAND_DEPENDENT_CATEGORIES).includes(selectedProduct.subCategory);

        // Check brand compatibility with ANY brand-dependent product
        if (isSelectedProductBrandDependent) {
          // Check if new product matches the ecosystem brand
          if (newProductBrand === selectedProductBrand) {
            sameBrand = true;
          } else {
            // Only add to conflicts if it's in the checkCategories (related products)
            if (checkCategories.includes(selectedProduct.subCategory)) {
              conflicts.push(selectedProduct);
            }
          }
        }
      }
    });

    return {
      compatible: conflicts.length === 0,
      conflicts: conflicts,
      sameBrand: sameBrand,
      recommendedBrand: recommendedBrand,
      productBrand: newProduct.brand[0]
    };
  }, []);

  // ============================================================================
  // PERFORMANCE OPTIMIZATION: Get product compatibility status WITH CACHING
  // ============================================================================
  const getProductCompatibilityStatus = useCallback((product, selectedProducts) => {
    // BYPASS: Locked categories (Chassis, UPS, etc.) and Chassis Upgrades are never subject to compatibility checks
    if (isLockedCategory(product.mainCategory) || product.mainCategory === 'Chassis Upgrades') {
      return {
        status: 'neutral',
        className: 'border border-gray-200 bg-white hover:border-gray-300',
        badgeColor: null,
        badgeText: null,
        buttonStyle: 'bg-black hover:bg-gray-800',
        buttonText: 'Add',
        opacity: 'opacity-100',
        showWarning: false,
        warningMessage: null
      };
    }
    
    // CHECK DISPLAY COMPATIBILITY FIRST
    const displayCheck = checkDisplayCompatibility(product, selectedProducts);
    
    if (!displayCheck.compatible) {
      return {
        status: 'display_incompatible',
        className: 'border border-gray-200 bg-white hover:border-gray-300',
        badgeColor: null,
        badgeText: null,
        buttonStyle: 'bg-black hover:bg-gray-800',
        buttonText: 'Add',
        opacity: 'opacity-100',
        showWarning: true,
        warningMessage: `This ${displayCheck.newProductType} display product conflicts with your current ${displayCheck.existingType} display setup. Switch to ${displayCheck.newProductType} display ecosystem to use this product.`
      };
    }
    
    // CHECK SIZE COMPATIBILITY SECOND
    const sizeCheck = checkSizeCompatibility(product, selectedProducts);
    
    if (!sizeCheck.compatible) {
      const conflictData = sizeCheck.conflicts[0];
      const warningMessage = `Size incompatible: ${conflictData.message}`;
      
      return {
        status: 'size_incompatible',
        className: 'border border-gray-200 bg-white hover:border-gray-300',
        badgeColor: null,
        badgeText: null,
        buttonStyle: 'bg-black hover:bg-gray-800',
        buttonText: 'Add',
        opacity: 'opacity-100',
        showWarning: true,
        warningMessage: warningMessage
      };
    }
    
    // THEN CHECK BRAND COMPATIBILITY
    const brandCheck = checkBrandCompatibility(product, selectedProducts);
    
    // If there's a recommended brand from selected products
    if (brandCheck.recommendedBrand) {
      if (brandCheck.sameBrand && brandCheck.compatible) {
        return {
          status: 'preferred',
          className: 'border border-gray-200 bg-white hover:border-gray-300',
          badgeColor: null,
          badgeText: null,
          buttonStyle: 'bg-black hover:bg-gray-800',
          buttonText: 'Add',
          opacity: 'opacity-100',
          showWarning: false,
          warningMessage: null
        };
      } else if (!brandCheck.compatible) {
        return {
          status: 'brand_incompatible',
          className: 'border border-gray-200 bg-white hover:border-gray-300',
          badgeColor: null,
          badgeText: null,
          buttonStyle: 'bg-black hover:bg-gray-800',
          buttonText: 'Add',
          opacity: 'opacity-100',
          showWarning: true,
          warningMessage: `This ${brandCheck.productBrand} product is not compatible with your ${brandCheck.recommendedBrand} ecosystem. Remove the ${brandCheck.recommendedBrand} products first to select ${brandCheck.productBrand} products.`
        };
      } else {
        return {
          status: 'different-brand',
          className: 'border border-gray-200 bg-white hover:border-gray-300',
          badgeColor: null,
          badgeText: null,
          buttonStyle: 'bg-black hover:bg-gray-800',
          buttonText: 'Add',
          opacity: 'opacity-100',
          showWarning: false,
          warningMessage: null
        };
      }
    }
    
    // No constraints - neutral status
    return {
      status: 'neutral',
      className: 'border border-gray-200 bg-white hover:border-gray-300',
      badgeColor: null,
      badgeText: null,
      buttonStyle: 'bg-black hover:bg-gray-800',
      buttonText: 'Add',
      opacity: 'opacity-100',
      showWarning: false,
      warningMessage: null
    };
  }, [checkDisplayCompatibility, checkSizeCompatibility, checkBrandCompatibility]);

  // ============================================================================
  // PERFORMANCE OPTIMIZATION: Sort products with PRE-COMPUTED scores
  // ============================================================================
  const sortProductsByCompatibility = useCallback((products, selectedProducts) => {
    // STEP 1: Pre-compute compatibility status for ALL products ONCE
    // This replaces calling getProductCompatibilityStatus twice per comparison
    const productsWithStatus = products.map(product => {
      const status = getProductCompatibilityStatus(product, selectedProducts);
      const score = COMPATIBILITY_SCORES[status.status] ?? 999;

      return {
        product,
        status,
        score
      };
    });
    
    // STEP 2: Sort using pre-computed scores (MUCH faster)
    const sortedWithStatus = [...productsWithStatus].sort((a, b) => {
      // Primary sort: compatibility score
      if (a.score !== b.score) {
        return a.score - b.score;
      }
      
      // Secondary sort: price
      const aPriceNum = parsePrice(a.product.price);
      const bPriceNum = parsePrice(b.product.price);
      
      if (aPriceNum !== bPriceNum) {
        return aPriceNum - bPriceNum;
      }
      
      // Tertiary sort: alphabetical by name
      return a.product.name.localeCompare(b.product.name);
    });
    
    // STEP 3: Return just the products (status already computed)
    return sortedWithStatus.map(item => item.product);
  }, [getProductCompatibilityStatus]);

  // Check if a product is compatible with current selection (legacy compatibility)
  const isProductCompatible = useCallback((product) => {
    const status = getProductCompatibilityStatus(product, selectedProducts);
    return status.status === 'preferred' || status.status === 'neutral' || status.status === 'different-brand';
  }, [selectedProducts, getProductCompatibilityStatus]);

  // Check if required products are already selected
  const checkRequirements = useCallback((product) => {
    if (!product.requires || !Array.isArray(product.requires) || product.requires.length === 0) {
      return { satisfied: true, missing: [] };
    }

    const missing = [];
    product.requires.forEach(requiredProductSlug => {
      const isSelected = Object.values(selectedProducts).some(
        selectedProduct => selectedProduct.slug === requiredProductSlug
      );
      
      if (!isSelected) {
        const requiredProduct = findProductBySlug(requiredProductSlug);
        if (requiredProduct) {
          missing.push(requiredProduct);
        }
      }
    });

    return {
      satisfied: missing.length === 0,
      missing: missing
    };
  }, [selectedProducts, findProductBySlug]);

  // Calculate total price
  const totalPrice = useCallback(() => {
    return Object.values(selectedProducts).reduce((total, product) => {
      return total + (product?.price ? parsePrice(product.price) : 0);
    }, 0);
  }, [selectedProducts]);

  // Calculate total test price (for admin mode) - only products with test prices
  const totalTestPrice = useCallback(() => {
    return Object.values(selectedProducts).reduce((total, product) => {
      // Only add test price if it exists, don't fall back to regular price
      if (product?.testPrice !== null && product?.testPrice !== undefined) {
        return total + parsePrice(product.testPrice);
      }
      return total;
    }, 0);
  }, [selectedProducts]);

  // Add product with its requirements
  const addProductWithRequirements = useCallback((originalProduct, missingProducts, mainCategory, subCategory) => {
    setSelectedProducts(prev => {
      const updated = { ...prev };
      
      // CRITICAL: If adding a SEAT, remove any existing seats first!
      const isSeat = isSeatsCategory(mainCategory);
      if (isSeat) {
        Object.keys(updated).forEach(key => {
          const { mainCategory: keyMainCategory } = parseProductKey(key);
          if (isSeatsCategory(keyMainCategory)) {
            delete updated[key];
          }
        });
      }
      
      // Add all missing required products with auto-added flag
      missingProducts.forEach(requiredProduct => {
        const key = createProductKey(requiredProduct.mainCategory, requiredProduct.foundInSubCategory);
        updated[key] = {
          ...requiredProduct,
          wasAutoAdded: true
        };
      });
      
      // Check if this category or subcategory allows multiple selections
      const isWheelRim = isWheelRimCategory(subCategory);
      const allowsMultiple = isWheelRim ||
                             allowsMultipleSelection(subCategory, categoryConfig) ||
                             allowsMultipleSelection(mainCategory, categoryConfig);

      if (allowsMultiple) {
        // For multiple selection categories/subcategories, create unique keys to allow multiple selections
        const uniqueKey = `${createProductKey(mainCategory, subCategory)}_${originalProduct.id}`;
        updated[uniqueKey] = {
          ...originalProduct,
          wasAutoAdded: false
        };
      } else {
        // Add the original product (manually selected)
        const originalKey = createProductKey(mainCategory, subCategory);
        updated[originalKey] = {
          ...originalProduct,
          wasAutoAdded: false
        };
      }

      return updated;
    });
  }, [categoryConfig]);

  // Switch brand ecosystem - removes all brand-dependent products and adds new one
  const switchBrandEcosystem = useCallback((newProduct) => {
    // Remove ALL brand-dependent products (both wheel base AND wheel rim)
    const brandDependentCategories = [SUBCATEGORIES.WHEEL_BASE, SUBCATEGORIES.WHEEL_RIM];

    setSelectedProducts(prev => {
      const updated = { ...prev };

      // Remove all brand-dependent products
      Object.keys(updated).forEach(productKey => {
        const product = updated[productKey];
        if (brandDependentCategories.includes(product.subCategory)) {
          delete updated[productKey];
        }
      });

      // Add the new product
      const isWheelRim = isWheelRimCategory(newProduct.subCategory);
      const allowsMultiple = isWheelRim ||
                             allowsMultipleSelection(newProduct.subCategory, categoryConfig) ||
                             allowsMultipleSelection(newProduct.mainCategory, categoryConfig);

      const categoryKey = allowsMultiple
        ? `${createProductKey('Racing Controls', newProduct.subCategory)}_${newProduct.id}`
        : createProductKey('Racing Controls', newProduct.subCategory);

      updated[categoryKey] = {
        ...newProduct,
        wasAutoAdded: false
      };

      return updated;
    });
  }, [categoryConfig]);

  // Switch display ecosystem with correct category name
  const switchDisplayEcosystem = useCallback((newProduct, newDisplayType) => {
    setSelectedProducts(prev => {
      const updated = { ...prev };
      
      // Remove ALL display products that conflict with the new type
      Object.keys(updated).forEach(productKey => {
        const product = updated[productKey];
        const productDisplayType = getDisplayType(product);
        
        // Remove if it's a display product with a conflicting type
        if (productDisplayType && productDisplayType !== newDisplayType) {
          delete updated[productKey];
        }
      });
      
      // Use the product's actual mainCategory instead of hardcoded 'Display & Audio'
      const categoryKey = createProductKey(newProduct.mainCategory, newProduct.subCategory);
      updated[categoryKey] = {
        ...newProduct,
        wasAutoAdded: false
      };
      
      return updated;
    });
  }, [getDisplayType]);

  // ============================================================================
  // PERFORMANCE OPTIMIZATION: Toggle product with ALL compatibility checks
  // SEATS: Only allow ONE seat at a time (replace any existing seat from ANY subcategory)
  // WHEEL RIMS: Allow multiple selections
  // ============================================================================
  const toggleProduct = useCallback((product, mainCategory, subCategory) => {
    // Prevent changes to locked products (chassis, UPS, etc.)
    if (isLockedProduct(product, mainCategory, subCategory)) {
      return { 
        success: false, 
        lockedProduct: true,
        message: "You can't change this - it's pre-selected" 
      };
    }

    // SEATS LOGIC: Only allow ONE seat at a time (across ALL subcategories)
    const isSeat = isSeatsCategory(mainCategory);

    if (isSeat) {
      const categoryKey = createProductKey(mainCategory, subCategory);
      const isAlreadySelected = selectedProducts[categoryKey]?.id === product.id;
      
      if (isAlreadySelected) {
        // Remove the selected seat
        setSelectedProducts(prev => {
          const updated = { ...prev };
          delete updated[categoryKey];
          return updated;
        });
        return { success: true };
      }
      
      // CHECK REQUIREMENTS FIRST (e.g., Formula seats need chassis upgrades)
      const requirementCheck = checkRequirements(product);

      if (!requirementCheck.satisfied) {
        return {
          success: false,
          requirementData: {
            product: product,
            missingProducts: requirementCheck.missing
          }
        };
      }
      
      // Requirements satisfied - Remove ANY existing seat and add new one
      setSelectedProducts(prev => {
        const updated = { ...prev };
        
        // Remove ALL seat products (General, Formula, or any other seat subcategory)
        Object.keys(updated).forEach(key => {
          const { mainCategory: keyMainCategory } = parseProductKey(key);
          if (isSeatsCategory(keyMainCategory)) {
            delete updated[key];
          }
        });

        // Now add the new seat (with its specific subcategory)
        updated[categoryKey] = {
          ...product,
          wasAutoAdded: false
        };
        
        return updated;
      });
      
      return { success: true };
    }

    // MULTIPLE SELECTION LOGIC: Check if this category or subcategory allows multiple selections
    // This includes Wheel Rims (hardcoded) OR any category/subcategory with allowMultipleSelection=true in CMS
    const isWheelRim = isWheelRimCategory(subCategory);
    const allowsMultiple = isWheelRim ||
                           allowsMultipleSelection(subCategory, categoryConfig) ||
                           allowsMultipleSelection(mainCategory, categoryConfig);

    if (allowsMultiple) {
      // For wheel rims, check if this specific product is already selected
      const uniqueKey = `${createProductKey(mainCategory, subCategory)}_${product.id}`;
      const isAlreadySelected = selectedProducts[uniqueKey];
      
      if (isAlreadySelected) {
        // Remove this specific wheel rim
        setSelectedProducts(prev => {
          const updated = { ...prev };
          delete updated[uniqueKey];
          return updated;
        });
        return { success: true };
      }
      
      // Check display TYPE compatibility FIRST
      const displayCheck = checkDisplayCompatibility(product, selectedProducts);
      
      if (!displayCheck.compatible) {
        return {
          success: false,
          displayConflictData: {
            newProduct: product,
            existingProduct: displayCheck.conflicts[0],
            newProductType: displayCheck.newProductType,
            existingType: displayCheck.existingType,
            message: `${displayCheck.newProductType} displays cannot be used with ${displayCheck.existingType} display stands/screens`
          }
        };
      }

      // Check SIZE compatibility SECOND
      const sizeCheck = checkSizeCompatibility(product, selectedProducts);
      
      if (!sizeCheck.compatible) {
        return {
          success: false,
          sizeConflictData: {
            newProduct: product,
            existingProduct: sizeCheck.conflicts[0].existingProduct,
            message: sizeCheck.conflicts[0].message
          }
        };
      }
      
      // Check brand compatibility before adding
      const brandCheck = checkBrandCompatibility(product, selectedProducts);
      
      if (!brandCheck.compatible) {
        return {
          success: false,
          brandConflictData: {
            product: product,
            conflictingProducts: brandCheck.conflicts
          }
        };
      }
      
      // Check requirements before adding
      const requirementCheck = checkRequirements(product);
      
      if (!requirementCheck.satisfied) {
        return { 
          success: false, 
          requirementData: {
            product: product,
            missingProducts: requirementCheck.missing
          }
        };
      }
      
      // Add the wheel rim with unique key
      setSelectedProducts(prev => ({
        ...prev,
        [uniqueKey]: {
          ...product,
          wasAutoAdded: false
        }
      }));
      
      return { success: true };
    } else {
      // Original single-select logic for all other categories
      const categoryKey = createProductKey(mainCategory, subCategory);
      const isAlreadySelected = selectedProducts[categoryKey]?.id === product.id;
      
      if (isAlreadySelected) {
        // Remove from selection
        setSelectedProducts(prev => {
          const updated = { ...prev };
          delete updated[categoryKey];
          return updated;
        });
        return { success: true };
      }
      
      // Check display TYPE compatibility FIRST
      const displayCheck = checkDisplayCompatibility(product, selectedProducts);
      
      if (!displayCheck.compatible) {
        return {
          success: false,
          displayConflictData: {
            newProduct: product,
            existingProduct: displayCheck.conflicts[0],
            newProductType: displayCheck.newProductType,
            existingType: displayCheck.existingType,
            message: `${displayCheck.newProductType} displays cannot be used with ${displayCheck.existingType} display stands/screens`
          }
        };
      }

      // Check SIZE compatibility SECOND
      const sizeCheck = checkSizeCompatibility(product, selectedProducts);
      
      if (!sizeCheck.compatible) {
        return {
          success: false,
          sizeConflictData: {
            newProduct: product,
            existingProduct: sizeCheck.conflicts[0].existingProduct,
            message: sizeCheck.conflicts[0].message
          }
        };
      }
      
      // Check requirements before adding
      const requirementCheck = checkRequirements(product);
      
      if (!requirementCheck.satisfied) {
        return { 
          success: false, 
          requirementData: {
            product: product,
            missingProducts: requirementCheck.missing
          }
        };
      }

      // Check brand compatibility
      const brandCheck = checkBrandCompatibility(product, selectedProducts);
      
      if (!brandCheck.compatible) {
        return {
          success: false,
          brandConflictData: {
            product: product,
            conflictingProducts: brandCheck.conflicts
          }
        };
      }
      
      // All checks passed, add the product
      setSelectedProducts(prev => ({
        ...prev,
        [categoryKey]: {
          ...product,
          wasAutoAdded: false
        }
      }));
      
      return { success: true };
    }
  }, [selectedProducts, checkRequirements, checkBrandCompatibility, checkDisplayCompatibility, checkSizeCompatibility, categoryConfig]);

  // Remove product to handle multi-select wheel rims
  const removeProduct = useCallback((productKey) => {
    // Handle both regular keys and multi-select keys (wheel rims have _productId suffix)
    const baseKey = productKey.split('_')[0]; // Remove product ID suffix if present
    const { mainCategory, subCategory } = parseProductKey(baseKey);

    // Prevent removal of locked products (chassis, UPS, etc.)
    if (isLockedCategory(mainCategory) || isLockedCategory(subCategory)) {
      return {
        success: false,
        message: "You can't change this - it's pre-selected"
      };
    }

    let removedProduct = null;
    setSelectedProducts(prev => {
      const updated = { ...prev };
      removedProduct = prev[productKey]; // Store the removed product
      delete updated[productKey];
      return updated;
    });

    return { success: true, removedProduct };
  }, []);

  // Auto-select default locked products when products load (chassis, UPS, etc.)
  const initializeWithLockedProducts = useCallback((productData) => {
    // Find locked categories and auto-select the first product from each
    Object.keys(productData).forEach(mainCategory => {
      if (isLockedCategory(mainCategory)) {
        Object.keys(productData[mainCategory]).forEach(subCategory => {
          const lockedProducts = productData[mainCategory][subCategory];
          if (lockedProducts && lockedProducts.length > 0) {
            const defaultProduct = lockedProducts[0];
            const categoryKey = createProductKey(mainCategory, subCategory);
            
            setSelectedProducts(prev => ({
              ...prev,
              [categoryKey]: {
                ...defaultProduct,
                wasAutoAdded: false,
                isLocked: true
              }
            }));
          }
        });
      }
    });
  }, []);

  // Set pre-selected products (for base config)
  const setPreselectedProducts = useCallback((products) => {
    setSelectedProducts(products);
  }, []);

  return {
    selectedProducts,
    totalPrice: totalPrice(),
    totalTestPrice: totalTestPrice(),
    checkRequirements,
    checkBrandCompatibility,
    checkDisplayCompatibility,
    checkSizeCompatibility,
    getDisplayType,
    areDisplayTypesCompatible,
    isProductCompatible,
    getProductCompatibilityStatus,
    sortProductsByCompatibility,
    addProductWithRequirements,
    toggleProduct,
    removeProduct,
    setPreselectedProducts,
    findProductBySlug,
    initializeWithLockedProducts,
    switchBrandEcosystem,
    switchDisplayEcosystem,
    displayDependentCategories: DISPLAY_DEPENDENT_CATEGORIES
  };
};